import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import api from '../api/axiosConfig';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';

/* ✅ safety: avoid "/api/api/..." */
function safeApiPath(path) {
  const p = String(path || '');
  if (!p) return p;

  const normalized = p.replace(/\/{2,}/g, '/');

  if (normalized.startsWith('/api/')) return normalized.replace(/^\/api/, '');
  if (normalized.startsWith('api/')) return normalized.replace(/^api/, '');

  if (normalized === '/api') return '/';
  if (normalized === 'api') return '/';

  return normalized;
}

/* ============== API ROUTES (EDIT IF NEEDED) ============== */
const API = {
  // idSlug может быть username или slug — ты решаешь на бэке
  // было: `/api/users/public/...`
  publicProfile: (idSlug) => safeApiPath(`/users/public/${encodeURIComponent(idSlug)}/`),
  // было: `/api/catalog/by-user/...`
  userProducts: (idSlug) => safeApiPath(`/catalog/by-user/${encodeURIComponent(idSlug)}/`),
};

/* ================== UI ================== */

const Page = styled.div`
  width: min(100%, 1800px);
  margin: clamp(12px, 2.8vw, 28px) auto;
  display: grid;
  gap: 18px;
`;

const Hero = styled.section`
  border-radius: 34px;
  padding: clamp(18px, 3vw, 30px);
  background: linear-gradient(180deg, #0b1b2b, #071321);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: grid;
  gap: 10px;
`;

const H1 = styled.h1`
  margin: 0;
  color: #fff;
  font-weight: 950;
  font-size: clamp(28px, 3.6vw, 44px);
  overflow-wrap: anywhere;
`;

const Sub = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  max-width: 820px;
  line-height: 1.6;
  white-space: pre-line;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const Badge = styled.div`
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 950;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.16);
  color: #fff;
`;

const Card = styled.section`
  background: rgba(255, 255, 255, 0.96);
  border-radius: 22px;
  padding: 16px;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.16);
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: grid;
  gap: 12px;
`;

const Title = styled.h2`
  margin: 0;
  color: #0b1b2b;
  font-weight: 950;
  font-size: 18px;
`;

const Grid = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Tile = styled.div`
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.14);
  display: flex;
  flex-direction: column;
`;

const Preview = styled.div`
  aspect-ratio: 16/9;
  background: linear-gradient(135deg, #0b1b2b, #102c45);
`;

const PreviewImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Body = styled.div`
  padding: 14px;
  display: grid;
  gap: 10px;
`;

const Name = styled.div`
  font-weight: 950;
  color: #0b1b2b;
  overflow-wrap: anywhere;
`;

const Meta = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.55);
`;

const BtnRow = styled.div`
  margin-top: auto;
  padding: 0 14px 14px;
  display: flex;
  gap: 10px;
`;

const Btn = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 14px;
  padding: 10px 12px;
  font-weight: 950;
  background: rgba(0, 0, 0, 0.08);
  color: #0b1b2b;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Primary = styled(Btn)`
  background: #ff8a1f;
  color: #111;
`;

const Small = styled.div`
  color: rgba(0, 0, 0, 0.7);
  font-size: 13px;
  line-height: 1.55;
`;

const API_ORIGIN = 'http://127.0.0.1:8000';
const toMediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return API_ORIGIN + url;
  return `${API_ORIGIN}/media/${url}`;
};

export default function PublicUserPage() {
  const { t } = useTranslation();
  const { idSlug } = useParams();
  const navigate = useNavigate();

  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        const [pRes, prRes] = await Promise.all([
          api.get(API.publicProfile(idSlug)),
          api.get(API.userProducts(idSlug)),
        ]);

        if (!alive) return;
        setProfile(pRes.data);
        setProducts(
          Array.isArray(prRes.data)
            ? prRes.data
            : prRes.data?.results || prRes.data?.products || [],
        );
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setProfile(null);
        setProducts([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [idSlug]);

  if (loading) return null;

  if (!profile) {
    return (
      <Page>
        <SEO
          title={t('publicUser.seoTitle')}
          description={t('publicUser.seoDesc')}
          path={`/u/${idSlug}`}
        />
        <Hero>
          <H1>{t('publicUser.notFoundTitle')}</H1>
          <Sub>{t('publicUser.notFoundText')}</Sub>
          <Row>
            <Primary onClick={() => navigate('/catalog')}>{t('publicUser.goCatalog')}</Primary>
          </Row>
        </Hero>
      </Page>
    );
  }

  return (
    <Page>
      <SEO
        title={t('publicUser.seoTitleWithName', { name: profile.display_name || idSlug })}
        description={t('publicUser.seoDesc')}
        path={`/u/${idSlug}`}
      />

      <Hero>
        <H1>{profile.display_name || idSlug}</H1>
        <Sub>{profile.bio || ''}</Sub>
        <Row>
          {profile.is_verified && <Badge>{t('publicUser.badges.verified')}</Badge>}
          {profile.role && <Badge>{profile.role}</Badge>}
        </Row>
      </Hero>

      <Card>
        <Title>{t('publicUser.productsTitle')}</Title>

        {products.length === 0 ? (
          <Small>{t('publicUser.productsEmpty')}</Small>
        ) : (
          <Grid>
            {products.map((p) => {
              const img = toMediaUrl(p.image_thumb || p.image);
              return (
                <Tile key={p.id}>
                  <Preview>
                    {img ? (
                      <PreviewImg src={img} alt={p.title} loading="lazy" decoding="async" />
                    ) : null}
                  </Preview>
                  <Body>
                    <Name>{p.title}</Name>
                    <Meta>
                      {p.price_from_eur != null
                        ? t('publicUser.priceFrom', { price: p.price_from_eur })
                        : t('common.dash')}
                    </Meta>
                  </Body>
                  <BtnRow>
                    <Btn
                      disabled={!p.demo_url}
                      onClick={() =>
                        p.demo_url && window.open(p.demo_url, '_blank', 'noopener,noreferrer')
                      }
                    >
                      {t('publicUser.demo')}
                    </Btn>
                    <Primary onClick={() => navigate(`/catalog?product=${p.id}`)}>
                      {t('publicUser.details')}
                    </Primary>
                  </BtnRow>
                </Tile>
              );
            })}
          </Grid>
        )}
      </Card>
    </Page>
  );
}
